import DOMPurify from 'dompurify';
import { emailCompositionDefaults, mergeEmailState } from '@kula-ai/components';
import type { EmailCompositionValue } from '@kula-ai/components';
import type { EmailCommConfigPayload } from '@/api/useJobStageActions/useCreateJobStageAction';
import {
  SHARED_EMAIL_VALUE_PREFIX,
  parseSharedEmailId,
} from '@/pages/InterviewManager/ScheduleInterview/hooks/useScheduleSenderOptions';
import type { InterviewEditorAttachmentItem } from '../types';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from '../types';

export const EMAIL_COMPOSITION_TO_MODE = 'single' as const;

/**
 * Sanitize an HTML body coming from the backend before it reaches the
 * editor. Mirrors legacy `FormEditor`'s use of `sanitizeDom` on tiptap
 * initial content — strips `<script>`, event handlers, and any other
 * DOMPurify-flagged dangerous markup. Skips legacy's newline→`<br>`
 * conversion because the new wizard's bodies are already HTML and
 * expanding `\n` would inject phantom line breaks.
 *
 * `ADD_ATTR: ['target', 'rel']` keeps link-extension attributes the
 * editor sets for new external links so an unedited body doesn't lose
 * them on round-trip.
 */
function sanitizeBodyHtml(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  return DOMPurify.sanitize(value, { ADD_ATTR: ['target', 'rel'] });
}

interface StaleSenderCleanupOptions {
  fromOptionValues: Set<string>;
  ccBccOptionValues: Set<string>;
}

/**
 * After hydration, drop any `from` / `cc` / `bcc` values that no longer
 * match an option in the dropdown. Prevents the "user removed from
 * org" failure mode where the form silently carries an id that the
 * UI dropdown can't render — the recruiter sees an empty From, but
 * save still ships the stale id and the backend rejects it. By
 * clearing the stale value here, validation surfaces it as a
 * required-field error instead.
 *
 * Returns a new composition with cleaned values, or `null` when the
 * input is already valid (so callers can `if (next) dispatch(...)`
 * without false-positive renders).
 */
export function cleanupStaleSenderValues(
  composition: EmailCompositionValue,
  options: StaleSenderCleanupOptions
): EmailCompositionValue | null {
  const { fromOptionValues, ccBccOptionValues } = options;
  // Skip cleanup when both option lists are empty — most likely the
  // sender API errored or hasn't returned yet. Wiping valid prefilled
  // ids in that state would be a regression worse than the stale-id
  // bug this helper fixes.
  if (fromOptionValues.size === 0 && ccBccOptionValues.size === 0) {
    return null;
  }
  let changed = false;
  const next: EmailCompositionValue = { ...composition };

  // The organizer sentinel resolves to "whoever schedules" at send
  // time and isn't in fromOptions for ScheduleInterview. Treat it as
  // valid so it isn't accidentally cleared by this cleanup before the
  // existing seed effect has had a chance to replace it.
  if (
    next.from &&
    next.from !== INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE &&
    !fromOptionValues.has(next.from)
  ) {
    next.from = '';
    changed = true;
  }

  if (Array.isArray(next.cc) && next.cc.length > 0) {
    const filtered = next.cc.filter((v) => ccBccOptionValues.has(v));
    if (filtered.length !== next.cc.length) {
      next.cc = filtered;
      changed = true;
    }
  }

  if (Array.isArray(next.bcc) && next.bcc.length > 0) {
    const filtered = next.bcc.filter((v) => ccBccOptionValues.has(v));
    if (filtered.length !== next.bcc.length) {
      next.bcc = filtered;
      changed = true;
    }
  }

  return changed ? next : null;
}

/**
 * `emailCompositionToCommConfig` often emits objects with only `undefined` values; those are still
 * truthy and break `Object.keys` empty-checks. Treat as “no server data” so we keep editor defaults.
 */
/** True if the API/config blob has no meaningful subject, body, sender, or recipients. */
export function isVacuousEmailCommConfig(
  config: EmailCommConfigPayload | null | undefined
): boolean {
  if (config === null) {
    return true;
  }
  if (typeof config !== 'object' || Array.isArray(config)) {
    return true;
  }
  if (Object.keys(config as object).length === 0) {
    return true;
  }
  const c = config as Record<string, unknown>;
  const subject = c.subject;
  const body = c.body;
  const hasSubject = typeof subject === 'string' && subject.trim() !== '';
  const hasBody = typeof body === 'string' && body.trim() !== '';
  const hasFrom = c.from !== null && c.from !== '';
  const fromOrg = c.from_organizer === true;
  const cc = c.cc;
  const bcc = c.bcc;
  const hasCc = Array.isArray(cc) && cc.length > 0;
  const hasBcc = Array.isArray(bcc) && bcc.length > 0;
  const atts = c.attachments;
  const hasAttachments = Array.isArray(atts) && atts.length > 0;
  return !(
    hasSubject ||
    hasBody ||
    hasFrom ||
    fromOrg ||
    hasCc ||
    hasBcc ||
    hasAttachments
  );
}

function parseIdList(ids: string[] | undefined): number[] | undefined {
  if (!ids?.length) {
    return undefined;
  }
  const nums = ids.map((s) => Number(s)).filter((n) => !Number.isNaN(n));
  return nums.length ? nums : undefined;
}

/**
 * Maps a Kula `Email` composition snapshot into API `EmailCommConfigPayload`.
 */
/**
 * Merges editor attachment chips into an email comm config for multipart.
 * FormData expects `…[attachments][]` = raw `File` or attachment id (see shared email flows).
 */
export function attachEditorItemsToCommConfig(
  base: EmailCommConfigPayload,
  items: InterviewEditorAttachmentItem[]
): EmailCommConfigPayload {
  if (!items.length) {
    return base;
  }
  const attachments: Array<File | number> = [];
  for (const item of items) {
    if (item.file instanceof File) {
      attachments.push(item.file);
      continue;
    }
    const idNum = Number(item.key);
    if (item.key !== '' && !Number.isNaN(idNum) && String(idNum) === item.key) {
      attachments.push(idNum);
    }
  }
  if (!attachments.length) {
    return base;
  }
  return { ...base, attachments };
}

export function emailCompositionToCommConfig(
  value: EmailCompositionValue,
  options?: { currentUserId?: number }
): EmailCommConfigPayload {
  const rawFrom = value.from !== null ? String(value.from).trim() : '';
  if (rawFrom === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE) {
    return {
      subject: value.subject?.trim() || undefined,
      body: value.content?.trim() ? value.content : undefined,
      from_organizer: true,
      cc: parseIdList(value.cc),
      bcc: parseIdList(value.bcc),
    };
  }
  const sharedEmailId = parseSharedEmailId(rawFrom);
  if (sharedEmailId !== undefined) {
    return {
      subject: value.subject?.trim() || undefined,
      body: value.content?.trim() ? value.content : undefined,
      from: options?.currentUserId,
      ats_shared_email_id: sharedEmailId,
      cc: parseIdList(value.cc),
      bcc: parseIdList(value.bcc),
    };
  }
  const from = rawFrom !== '' ? Number(rawFrom) : undefined;
  return {
    subject: value.subject?.trim() || undefined,
    body: value.content?.trim() ? value.content : undefined,
    from: from !== undefined && !Number.isNaN(from) ? from : undefined,
    cc: parseIdList(value.cc),
    bcc: parseIdList(value.bcc),
  };
}

/**
 * Hydrate `EmailCompositionValue` from API / GET config.
 * GET may nest `sender: { id }` while POST uses `from` / `from_organizer` — map both.
 * When no sender is stored, default to organizer (matches PUT `from_organizer: true` when user picks Organizer).
 */
export function commConfigToEmailComposition(
  _config: EmailCommConfigPayload | null | undefined
): EmailCompositionValue {
  if (isVacuousEmailCommConfig(_config)) {
    return { ...emailCompositionDefaults };
  }
  const raw = _config as EmailCommConfigPayload & Record<string, unknown>;

  const senderRaw = raw.sender;
  let senderId: number | undefined;
  if (
    senderRaw &&
    typeof senderRaw === 'object' &&
    !Array.isArray(senderRaw) &&
    'id' in senderRaw
  ) {
    const n = Number((senderRaw as { id: unknown }).id);
    if (!Number.isNaN(n)) {
      senderId = n;
    }
  }

  let sharedEmailId: number | undefined;
  const flatSharedId = raw.ats_shared_email_id;
  if (typeof flatSharedId === 'number' && flatSharedId > 0) {
    sharedEmailId = flatSharedId;
  } else {
    const sharedRaw = raw.ats_shared_email;
    if (
      sharedRaw &&
      typeof sharedRaw === 'object' &&
      !Array.isArray(sharedRaw) &&
      'id' in (sharedRaw as Record<string, unknown>)
    ) {
      const n = Number((sharedRaw as { id: unknown }).id);
      if (!Number.isNaN(n) && n > 0) {
        sharedEmailId = n;
      }
    }
  }

  let fromValue: string;
  if (sharedEmailId !== undefined) {
    fromValue = `${SHARED_EMAIL_VALUE_PREFIX}${sharedEmailId}`;
  } else if (raw.from_organizer === true) {
    fromValue = INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE;
  } else if (
    raw.from !== undefined &&
    raw.from !== null &&
    !Number.isNaN(Number(raw.from))
  ) {
    fromValue = String(raw.from);
  } else if (senderId !== undefined) {
    fromValue = String(senderId);
  } else {
    fromValue = INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE;
  }

  // CC/BCC must accept both shapes:
  //   - GET response (`_comm_configs.json.jbuilder`): `cc_participants` /
  //     `bcc_participants` — array of user objects `{ id, name, ... }`.
  //   - POST/PUT input shape: `cc` / `bcc` — array of numeric ids.
  // Without falling back to the participants arrays, the edit hydration
  // path silently strips CC/BCC: the GET payload never contains `cc`,
  // so `raw.cc ?? []` always returned `[]` and the next save dropped
  // every recipient that was set when the action was originally created.
  const toRecipientIds = (val: unknown): string[] => {
    if (!Array.isArray(val) || val.length === 0) return [];
    return (val as Array<unknown>)
      .map((entry) => {
        if (typeof entry === 'object' && entry !== null && 'id' in entry) {
          const id = (entry as { id: unknown }).id;
          return id !== undefined && id !== null ? String(id) : '';
        }
        return entry !== undefined && entry !== null ? String(entry) : '';
      })
      .filter(Boolean);
  };

  return mergeEmailState(
    { ...emailCompositionDefaults },
    {
      subject: raw.subject ?? '',
      content: sanitizeBodyHtml(raw.body),
      from: fromValue,
      cc: toRecipientIds(raw.cc_participants ?? raw.cc),
      bcc: toRecipientIds(raw.bcc_participants ?? raw.bcc),
    },
    EMAIL_COMPOSITION_TO_MODE
  );
}

export function getDefaultEmailComposition(): EmailCompositionValue {
  return { ...emailCompositionDefaults };
}

export function patchEmailComposition(
  base: EmailCompositionValue,
  patch: Partial<EmailCompositionValue>
): EmailCompositionValue {
  return mergeEmailState(base, patch, EMAIL_COMPOSITION_TO_MODE);
}
